import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { BellIcon, CloseIcon } from './icons';
import { route } from './HashRouter';
import type { Preview } from '../types';
import {
  fetchColdStorageSoon,
  fetchExpiringSoon,
  fetchAcknowledgedNotifications,
  fetchPreviewsBatch,
  acknowledgeNotification,
  hasProAccess,
  ArchiveNotification,
  NotificationAckResponse,
} from '../services/api';
import { useBilling } from '../contexts/BillingContext';

function notificationKey(n: ArchiveNotification): string {
  return `${n.topic}:${n.vertex}:${n.archiveTime}`;
}

function ackKey(a: NotificationAckResponse): string {
  return `${a.topic}:${a.vertex.rendered}:${a.archiveTime}`;
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function daysUntil(isoDate: string, limitSeconds: number): number {
  const archived = new Date(isoDate).getTime();
  const deadline = archived + limitSeconds * 1000;
  return Math.max(0, Math.ceil((deadline - Date.now()) / (24 * 60 * 60 * 1000)));
}

export function NotificationBell() {
  const [notifications, setNotifications] = useState<ArchiveNotification[]>([]);
  const [previews, setPreviews] = useState<Record<string, Preview>>({});
  const [dismissing, setDismissing] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { subscription, loading: billingLoading } = useBilling();

  useEffect(() => {
    if (billingLoading) return;
    loadNotifications();
  }, [billingLoading, subscription]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  async function loadNotifications() {
    try {
      const items: ArchiveNotification[] = [];

      if (!hasProAccess(subscription)) {
        const [coldRes, expiringRes] = await Promise.all([
          fetchColdStorageSoon('7d'),
          fetchExpiringSoon('7d'),
        ]);
        for (const [vertex, times] of Object.entries(coldRes.coldStorage)) {
          for (const archiveTime of times) {
            items.push({ vertex, archiveTime, topic: 'ColdStorage', limitSeconds: coldRes.coldStorageLimit });
          }
        }
        for (const [vertex, times] of Object.entries(expiringRes.expiring)) {
          for (const archiveTime of times) {
            items.push({ vertex, archiveTime, topic: 'Expiry', limitSeconds: expiringRes.expirationLimit });
          }
        }
      }

      if (items.length === 0) {
        setNotifications([]);
        return;
      }

      const acks = await fetchAcknowledgedNotifications();
      const ackedSet = new Set(acks.map(ackKey));
      const unacked = items.filter(n => !ackedSet.has(notificationKey(n)));
      setNotifications(unacked);

      const uniqueVertices = [...new Set(unacked.map(n => n.vertex))];
      if (uniqueVertices.length > 0) {
        const previewData = await fetchPreviewsBatch(uniqueVertices);
        setPreviews(previewData);
      }
    } catch {
      // Not logged in or endpoint not available yet
    }
  }

  function handleDismiss(n: ArchiveNotification, e: Event) {
    e.stopPropagation();
    const key = notificationKey(n);
    setDismissing(prev => new Set(prev).add(key));
    acknowledgeNotification({
      topic: n.topic,
      vertex: n.vertex,
      archiveTime: n.archiveTime,
    }).catch(err => console.error('[Vertex] Error acknowledging notification:', err));
    setTimeout(() => {
      setNotifications(prev => prev.filter(x => notificationKey(x) !== key));
      setDismissing(prev => { const next = new Set(prev); next.delete(key); return next; });
    }, 300);
  }

  function handleClick(n: ArchiveNotification) {
    const encodedVertex = encodeURIComponent(n.vertex);
    const encodedTimestamp = encodeURIComponent(n.archiveTime);
    const context = n.topic === 'ColdStorage' ? 'coldstorage' : 'expiring';
    // Preserve wherever the bell was clicked from so the archive's back button
    // returns there instead of the root list.
    const currentHash = window.location.hash.slice(1);
    const fromParam = currentHash && currentHash !== '/'
      ? `&from=${encodeURIComponent(currentHash)}`
      : '';
    setOpen(false);
    route(`/archive/${encodedVertex}/${encodedTimestamp}?context=${context}&limit=${n.limitSeconds}${fromParam}`);
  }

  function messageFor(n: ArchiveNotification): string {
    const days = daysUntil(n.archiveTime, n.limitSeconds);
    return `in ${days} day${days !== 1 ? 's' : ''}`;
  }

  const count = notifications.length;

  return (
    <div class="notification-bell" ref={ref}>
      <button
        class="btn-header-icon"
        title="Notifications"
        onClick={() => setOpen(!open)}
      >
        <BellIcon />
        {count > 0 && <span class="notification-count">{count}</span>}
      </button>
      {open && (
        <div class="notification-dropdown">
          {count === 0 ? (
            <div class="notification-empty">No notifications</div>
          ) : (
            notifications.map(n => {
              const preview = previews[n.vertex];
              const title = preview?.title || new URL(n.vertex).hostname;
              return (
                <div
                  key={notificationKey(n)}
                  class={`notification-item${dismissing.has(notificationKey(n)) ? ' notification-item-dismissing' : ''}`}
                  onClick={() => handleClick(n)}
                >
                  {preview?.image ? (
                    <img
                      class="notification-thumb"
                      src={preview.image}
                      alt=""
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div class="notification-thumb notification-thumb-empty" />
                  )}
                  <div class="notification-item-content">
                    <span class="notification-title" title={n.vertex}>{title}</span>
                    <span class={`notification-type notification-type-${n.topic === 'ColdStorage' ? 'coldstorage' : 'expiry'}`}>
                      {n.topic === 'ColdStorage' ? 'Cold storage soon' : 'Expiring soon'}
                      {' \u2014 '}{messageFor(n)}
                    </span>
                    <span class="notification-date">{formatDate(n.archiveTime)}</span>
                  </div>
                  <button
                    class="notification-dismiss"
                    onClick={(e) => handleDismiss(n, e)}
                    title="Dismiss"
                  >
                    <CloseIcon size={12} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
